import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  demandPartnerBidderConfigSchema,
  demandPartnerOwnersPageSchema,
  demandPartnerSchema,
  demandPartnerStatusCountsSchema,
  demandPartnersPageSchema,
  DemandPartnerStatus,
  DemandPartnerTargetingList,
  type DemandPartnerBidderConfig,
  type DemandPartnerOwner,
  type DemandPartnerMutationPayload,
  type DemandPartnersFilter,
} from './model';

interface DemandPartnersClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const mutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), z.looseObject({ id: z.coerce.string().optional() })]),
);
const targetingListImportResponseSchema = z.number().int().nonnegative();
const targetingListPath = {
  [DemandPartnerTargetingList.Bundle]: 'bundle-list',
  [DemandPartnerTargetingList.Domain]: 'domain-list',
} as const;

export function createDemandPartnersClient({
  config,
  accessToken,
  fetcher,
}: DemandPartnersClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    list: (filter: DemandPartnersFilter, options?: { signal?: AbortSignal }) =>
      api.request({
        path: `/api/dps?${serializeDemandPartnersFilter(filter)}`,
        schema: demandPartnersPageSchema,
        ...(options?.signal ? { init: { signal: options.signal } } : {}),
      }),
    search: (searchTerm: string, selectedId?: string) =>
      api.request({
        path: `/api/dps?${toSearchParams({
          page: 0,
          size: 25,
          displayName: searchTerm || undefined,
          ids: selectedId || undefined,
          status: [DemandPartnerStatus.Active, DemandPartnerStatus.Inactive],
        })}`,
        schema: demandPartnersPageSchema,
      }),
    getStatusCounts: (filter: Pick<DemandPartnersFilter, 'searchTerm'>) =>
      api.request({
        path: `/api/dps/statuses?${toSearchParams({
          displayName: filter.searchTerm || undefined,
        })}`,
        schema: demandPartnerStatusCountsSchema,
      }),
    listOwners: async (): Promise<DemandPartnerOwner[]> => {
      const response = await api.request({
        path: `/api/owners?${toSearchParams({
          page: 0,
          size: 1000,
          sortProperty: 'name',
          sortDirection: 'ASC',
        })}`,
        schema: demandPartnerOwnersPageSchema,
      });
      return Array.isArray(response) ? response : response.content;
    },
    getBidderConfig: async (): Promise<DemandPartnerBidderConfig> =>
      api.request({
        path: '/api/dps/bidder-codes-exchange-config',
        schema: demandPartnerBidderConfigSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(id)}`,
        schema: demandPartnerSchema,
      }),
    create: (payload: DemandPartnerMutationPayload) =>
      api.request({
        path: '/api/dps',
        schema: mutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: (id: string, payload: DemandPartnerMutationPayload) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(id)}`,
        schema: mutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    downloadTargetingListTemplate: (list: DemandPartnerTargetingList) =>
      api.requestBlob({
        path: `/api/dps/csv/export-${targetingListPath[list]}-template`,
      }),
    downloadTargetingList: (id: string, list: DemandPartnerTargetingList) =>
      api.requestBlob({
        path: `/api/dps/${encodeURIComponent(id)}/csv/export-${targetingListPath[list]}`,
      }),
    uploadTargetingList: (id: string, list: DemandPartnerTargetingList, file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return api.request({
        path: `/api/dps/${encodeURIComponent(id)}/csv/import-${targetingListPath[list]}`,
        schema: targetingListImportResponseSchema,
        init: { method: 'POST', body },
      });
    },
  };
}

export function serializeDemandPartnersFilter(filter: DemandPartnersFilter) {
  return toSearchParams({
    displayName: filter.searchTerm,
    status: filter.statuses,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

export type DemandPartnersClient = ReturnType<typeof createDemandPartnersClient>;
